import React from "react";
import { Category } from "../models/category";

interface CategoryGridProps {
  categories: Category[];
  categoryTitle?: string;
  onCategoryClick?: (category: Category) => void;
  alternateStyle?: boolean;
  isSquare?: boolean;
  bypassLabel?: boolean;
}

export const CategoryGrid: React.FC<CategoryGridProps> = ({
  categories,
  categoryTitle,
  onCategoryClick: onCategorySelect,
  alternateStyle,
  isSquare,
  bypassLabel,
}) => {
  return (
    <section className="mx-auto max-w-7xl px-4 py-8 md:py-12">
      {categoryTitle && (
        <h2 className="relative mb-12 text-center text-3xl font-bold text-gray-800 md:text-4xl lg:text-5xl">
          {categoryTitle}
          <span className="absolute -bottom-6 left-1/2 h-1 w-24 -translate-x-1/2 transform rounded-full bg-gray-500" />
        </h2>
      )}

      <div
        className={`grid gap-3 md:gap-4 lg:gap-6 place-items-center ${
          // place-items centers both axes
          isSquare
            ? alternateStyle
              ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
              : "grid-cols-2 lg:grid-cols-4"
            : alternateStyle
            ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
            : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6"
        }`}
      >
        {categories.map((category) => (
          <div
            key={category.id}
            onClick={() => onCategorySelect?.(category)}
            className={`group relative cursor-pointer overflow-hidden rounded-2xl bg-gray-100 shadow-lg transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 ${
              isSquare && alternateStyle ? "rounded-2xl" : ""
            }`}
            style={
              isSquare && alternateStyle ? { aspectRatio: "4/2" } : undefined
            }
          >
            {/* Image Container - Conditional aspect ratio */}
            <div
              className={
                isSquare
                  ? alternateStyle
                    ? "overflow-hidden h-full"
                    : "aspect-square overflow-hidden"
                  : ""
              }
            >
              <img
                src={category.coverImageUrl}
                alt={category.name}
                className={`h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${
                  isSquare && alternateStyle ? "rounded-2xl" : ""
                }`}
              />
            </div>

            {/* Gradient Overlay */}
            {!bypassLabel && (
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-80 transition-opacity duration-300 group-hover:opacity-90" />
            )}

            {/* Content */}
            {!bypassLabel && (
              <div
                className={`absolute inset-x-0 bottom-0 ${
                  isSquare ? "px-4 pb-4 lg:px-6 lg:pb-6" : "p-3 md:p-4"
                } ${isSquare && alternateStyle ? "rounded-2xl" : ""}`}
              >
                <div className="transform transition-all duration-300 ease-out group-hover:-translate-y-1">
                  <h3
                    className={`font-bold text-white leading-tight mb-1 ${
                      isSquare
                        ? "text-lg lg:text-2xl"
                        : "text-sm md:text-base lg:text-lg"
                    }`}
                  >
                    {category.name}
                  </h3>
                </div>
              </div>
            )}

            {/* Hover Border Effect */}
            <div
              className={`absolute inset-0 border-2 border-transparent transition-all duration-300 group-hover:border-white/30 ${
                isSquare && alternateStyle ? "rounded-2xl" : "rounded-xl"
              }`}
            />

            {/* Subtle shine effect on hover */}
            <div
              className={`absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100 ${
                isSquare && alternateStyle ? "rounded-2xl" : "rounded-xl"
              }`}
            />
          </div>
        ))}
      </div>
    </section>
  );
};

export default CategoryGrid;
